if (!customElements.get('next-event')) {
  customElements.define(
    'next-event',
    class NextEvent extends HTMLElement {
      connectedCallback() {
        try {
          this.config = JSON.parse(this.querySelector('[data-next-event-config]')?.textContent || '');
        } catch {
          return;
        }

        this.locale = this.config.locale || document.documentElement.lang || 'en-AU';
        this.timeZone = this.config.timeZone || 'Australia/Sydney';
        this.copy = this.config.copy || {};
        this.fallback = this.querySelector('[data-event-fallback]');
        this.panel = this.querySelector('[data-event-panel]');
        this.countdown = this.querySelector('[data-event-countdown]');
        this.live = this.querySelector('[data-event-live]');
        this.startOutput = this.querySelector('[data-event-start]');
        this.finishOutput = this.querySelector('[data-event-finish]');
        this.schedule = this.readSchedule(this.config.event);
        this.events = new AbortController();

        document.addEventListener(
          'visibilitychange',
          () => {
            if (!document.hidden) this.update();
          },
          { signal: this.events.signal }
        );

        this.dataset.enhanced = '';
        this.update();
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        this.events?.abort();
      }

      now() {
        return new Date();
      }

      zonedParts(value) {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(value);
        const get = (type) => Number(parts.find((part) => part.type === type)?.value);
        return {
          year: get('year'),
          month: get('month'),
          day: get('day'),
          hour: get('hour'),
          minute: get('minute'),
        };
      }

      zonedDate(dateValue, timeValue) {
        const dateMatch = String(dateValue || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const timeMatch = String(timeValue || '').trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
        if (!dateMatch || !timeMatch) return null;

        const target = {
          year: Number(dateMatch[1]),
          month: Number(dateMatch[2]),
          day: Number(dateMatch[3]),
          hour: Number(timeMatch[1]),
          minute: Number(timeMatch[2]),
        };
        const wallTime = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute);
        const calendarCheck = new Date(wallTime);
        if (
          calendarCheck.getUTCFullYear() !== target.year ||
          calendarCheck.getUTCMonth() + 1 !== target.month ||
          calendarCheck.getUTCDate() !== target.day
        ) return null;

        let instant = wallTime;
        try {
          for (let attempt = 0; attempt < 4; attempt += 1) {
            const shown = this.zonedParts(new Date(instant));
            const shownAsUTC = Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute);
            const correction = wallTime - shownAsUTC;
            instant += correction;
            if (correction === 0) break;
          }
        } catch {
          return null;
        }

        const result = new Date(instant);
        try {
          const final = this.zonedParts(result);
          return Object.keys(target).every((key) => final[key] === target[key]) ? result : null;
        } catch {
          return null;
        }
      }

      readSchedule(event = {}) {
        const name = String(event.name || '').trim();
        if (!name) return null;

        const start = this.zonedDate(event.startDate, event.startTime);
        const finish = this.zonedDate(event.finishDate, event.finishTime);
        if (!start || !finish || finish <= start) return null;

        return {
          name,
          venue: String(event.venue || '').trim(),
          start,
          finish,
        };
      }

      stateAt(value, schedule = this.schedule) {
        const now = value instanceof Date ? value : new Date(value);
        if (!schedule || Number.isNaN(now.valueOf())) return 'fallback';
        if (now < schedule.start) return 'upcoming';
        if (now < schedule.finish) return 'live';
        return 'fallback';
      }

      countdownAt(value, start = this.schedule?.start) {
        const now = value instanceof Date ? value : new Date(value);
        if (!start || Number.isNaN(now.valueOf())) return null;

        let remaining = Math.ceil((start.valueOf() - now.valueOf()) / 1000);
        if (remaining <= 0) return null;
        const days = Math.floor(remaining / 86_400);
        remaining -= days * 86_400;
        const hours = Math.floor(remaining / 3_600);
        remaining -= hours * 3_600;
        const minutes = Math.floor(remaining / 60);
        const seconds = remaining - minutes * 60;
        return { days, hours, minutes, seconds };
      }

      format(key, values = {}) {
        let message = this.copy[key] || '';
        Object.entries(values).forEach(([name, value]) => {
          message = message.replaceAll(`[[${name}]]`, value);
        });
        return message;
      }

      unitLabel(unit, value) {
        const names = {
          days: 'day',
          hours: 'hour',
          minutes: 'minute',
          seconds: 'second',
        };
        const name = names[unit];
        if (!name) return unit;

        try {
          const parts = new Intl.NumberFormat(this.locale, {
            style: 'unit',
            unit: name,
            unitDisplay: 'long',
          }).formatToParts(value);
          const label = parts
            .filter((part) => part.type === 'unit')
            .map((part) => part.value)
            .join('');
          if (label) return label;
        } catch {
          // The visible English fallback below keeps the timer usable in a
          // browser with incomplete Intl unit support.
        }

        return value === 1 ? name : `${name}s`;
      }

      pretty(value) {
        return new Intl.DateTimeFormat(this.locale, {
          timeZone: this.timeZone,
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(value);
      }

      show(state) {
        this.dataset.state = state;
        if (this.fallback) this.fallback.hidden = state !== 'fallback';
        if (this.panel) this.panel.hidden = state === 'fallback';
        if (this.countdown) this.countdown.hidden = state !== 'upcoming';
        if (this.live) this.live.hidden = state !== 'live';
      }

      update() {
        window.clearTimeout(this.timer);
        const now = this.now();
        const state = this.stateAt(now);
        this.show(state);

        if (state === 'fallback') return;

        if (this.startOutput) {
          this.startOutput.dateTime = this.schedule.start.toISOString();
          this.startOutput.textContent = this.pretty(this.schedule.start);
        }
        if (this.finishOutput) {
          this.finishOutput.dateTime = this.schedule.finish.toISOString();
          this.finishOutput.textContent = this.pretty(this.schedule.finish);
        }

        if (state === 'live') {
          const untilFinish = this.schedule.finish.valueOf() - now.valueOf();
          this.timer = window.setTimeout(() => this.update(), Math.min(untilFinish + 25, 60_000));
          return;
        }

        const values = this.countdownAt(now);
        if (!values) {
          this.update();
          return;
        }

        const labels = {};
        for (const unit of ['days', 'hours', 'minutes', 'seconds']) {
          const output = this.querySelector(`[data-event-${unit}]`);
          if (output) output.textContent = values[unit];
          const label = this.unitLabel(unit, values[unit]);
          const labelOutput = this.querySelector(`[data-event-${unit}-label]`);
          if (labelOutput) labelOutput.textContent = label;
          labels[`${unit}_label`] = label;
        }
        if (this.countdown) {
          this.countdown.setAttribute('aria-label', this.format('countdown', { ...values, ...labels }));
        }

        const untilNextSecond = 1_000 - (now.valueOf() % 1_000) + 25;
        this.timer = window.setTimeout(() => this.update(), untilNextSecond);
      }
    }
  );
}
